import React, {useEffect, useState} from 'react';
import {useLocation} from 'react-router-dom';
import {Content} from 'antd/es/layout/layout';
import {Card, Tag, Button, Tooltip as AntdTooltip, Badge, Spin} from 'antd';
import {PieChart, Pie, Cell, Tooltip, Legend} from 'recharts';
import {CircularProgressbar, buildStyles} from 'react-circular-progressbar';
import {motion} from 'framer-motion';
import 'react-circular-progressbar/dist/styles.css';
import {LoadingOutlined} from '@ant-design/icons';
import AppHeader from '../../../components/Dashboard/AppHeader';
import {viewChapterReportAPI} from '../../../zustand/API';
import userStore from '../../../zustand/store/userStore';

const COLORS = ['#00C49F', '#FF4D4F'];

const ReportQuiz = () => {
	const location = useLocation();
	const {user} = userStore();
	const {report} = location.state;
	const [reportData, setReportData] = useState(null);

	useEffect(() => {
		const fetchReport = async () => {
			try {
				const response = await viewChapterReportAPI({
					email: report?.email || user?.email,
					subject: report?.subjectName,
					chapterName: report?.title ,
				});
				setReportData(response?.data?.report);
			} catch (error) {
				console.error('Error fetching report:', error);
			}
		};
		fetchReport();
	}, []);

	if (!reportData)
		return (
			<div className="flex items-center justify-center min-h-screen">
				<Spin
					size="large"
					indicator={
						<LoadingOutlined style={{fontSize: 36, color: '#22C55E'}} spin />
					}
					tip="Fetching your report..."
				/>
			</div>
		);

	const {
		summary,
		wrongAnswers,
		studentEmail,
		subject,
		chapterName,
		className,
	} = reportData;

	const pieData = [
		{name: 'Correct', value: summary.correctAnswers},
		{name: 'Incorrect', value: summary.totalQuestions - summary.correctAnswers},
	];

	const getStatusColorClasses = (color) => {
		switch (color?.toLowerCase()) {
			case 'green':
				return 'bg-green-100 border-green-500 text-green-900';
			case 'yellow':
				return 'bg-yellow-100 border-yellow-500 text-yellow-900';
			case 'red':
				return 'bg-red-100 border-red-500 text-red-900';
			default:
				return 'bg-gray-100 border-gray-400 text-gray-800';
		}
	};

	return (
		<Content className="p-6 min-h-screen bg-gradient-to-tr from-white via-blue-50 to-white">
			<AppHeader title="📚 Chapter Performance Summary" />

			<motion.div
				initial={{opacity: 0, y: -10}}
				animate={{opacity: 1, y: 0}}
				transition={{duration: 0.6}}
			>
				<Badge.Ribbon text="AI Suggestion" color={summary.statusColor}>
					<Card
						className={`mt-6 rounded-2xl shadow-md glass-effect border-l-4 ${getStatusColorClasses(
							summary.statusColor,
						)}`}
					>
						<p className="text-base">{summary.suggestion}</p>
					</Card>
				</Badge.Ribbon>
			</motion.div>

			<div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-8">
				<motion.div
					initial={{opacity: 0, y: 20}}
					animate={{opacity: 1, y: 0}}
					transition={{duration: 0.5, delay: 0.2}}
				>
					<Card
						title="🎓 Student Profile"
						className="glass-effect rounded-2xl shadow-lg backdrop-blur border border-white/40"
					>
						<p>
							<strong>Email:</strong> {studentEmail}
						</p>
						<p>
							<strong>Class:</strong> {className}
						</p>
						<p>
							<strong>Subject:</strong> {subject}
						</p>
						<p>
							<strong>Chapter:</strong> {chapterName}
						</p>
					</Card>
				</motion.div>

				<motion.div
					initial={{opacity: 0, y: 20}}
					animate={{opacity: 1, y: 0}}
					transition={{duration: 0.5, delay: 0.4}}
				>
					<Card
						title="🔥 Your Score"
						className="glass-effect rounded-2xl shadow-lg text-center border border-white/40"
						extra={<Tag color={summary.statusColor}>{summary.status}</Tag>}
					>
						<div className="flex justify-center">
							<div className="w-32 h-32">
								<CircularProgressbar
									value={parseFloat(summary.percentage)}
									text={`${summary.percentage}%`}
									styles={buildStyles({
										textColor: '#222',
										pathColor: summary.statusColor,
										trailColor: '#eee',
										textSize: '18px',
									})}
								/>
							</div>
						</div>
						<div className="mt-4 space-y-1 text-gray-700 text-sm">
							<p>
								<strong>Total:</strong> {summary.totalQuestions}
							</p>
							<p>
								<strong>Correct:</strong> {summary.correctAnswers}
							</p>
						</div>
					</Card>
				</motion.div>

				<motion.div
					initial={{opacity: 0, y: 20}}
					animate={{opacity: 1, y: 0}}
					transition={{duration: 0.5, delay: 0.6}}
				>
					<Card
						title="📊 Answer Distribution"
						className="glass-effect rounded-2xl shadow-lg border border-white/40"
					>
						<PieChart width={250} height={250}>
							<Pie
								data={pieData}
								cx="50%"
								cy="50%"
								innerRadius={50}
								outerRadius={80}
								fill="#8884d8"
								dataKey="value"
								label
							>
								{pieData.map((entry, index) => (
									<Cell
										key={`cell-${index}`}
										fill={COLORS[index % COLORS.length]}
									/>
								))}
							</Pie>
							<Tooltip />
							<Legend verticalAlign="bottom" height={36} />
						</PieChart>
					</Card>
				</motion.div>
			</div>

			{wrongAnswers?.length > 0 && (<motion.div
				initial={{opacity: 0, y: 30}}
				animate={{opacity: 1, y: 0}}
				transition={{duration: 0.6, delay: 0.7}}
			>
				<Card
					title={`❌ ${wrongAnswers?.length} Incorrect Questions`}
					className="mt-8 rounded-2xl border border-white/40 shadow-md max-h-[400px] overflow-auto"
					extra={
						<AntdTooltip title="Review these topics again.">
							<span className="text-sm text-gray-600 font-medium">
								Need Attention
							</span>
						</AntdTooltip>
					}
				>
					{wrongAnswers?.map((item, idx) => (
						<div
							key={item._id}
							className="bg-white/60 border-l-4 border-red-500 p-4 mb-4 rounded-md backdrop-blur-sm"
						>
							<p className="text-sm font-medium">
								{idx + 1}. {item.question}
							</p>
							<Tag color="red" className="mt-2">
								Topic: {item.topic}
							</Tag>
						</div>
					))}
				</Card>
			</motion.div>)}

			{/* <div className="mt-8 flex gap-4 justify-end">
				<Button
					icon={<RedoOutlined />}
					type="primary"
					size="large"
					className="shadow-md rounded-xl hover:scale-105 transition transform duration-200"
				>
					Retake Quiz
				</Button>
				<Button
					icon={<DownloadOutlined />}
					type="default"
					size="large"
					className="shadow-md rounded-xl hover:bg-blue-50 transition"
				>
					Export Report
				</Button>
			</div> */}
		</Content>
	);
};

export default ReportQuiz;
